import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import PersonSearchOutlined from "@mui/icons-material/PersonSearchOutlined";
import SearchOffRounded from "@mui/icons-material/SearchOffRounded";
import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { JumpingDots } from "../../../../components/UIToolkit/Animations/JumpingDots";
import MetaChip from "../../../../components/UIToolkit/Badges/MetaChip";
import { SystemChoiceList } from "../../../../components/UIToolkit/Clickable/SystemChoiceList";
import { SheetSearchField } from "../../../../components/UIToolkit/Inputs/SheetSearchField";
import { EmptyState } from "../../../../components/UIToolkit/molecules/EmptyState";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useLastNameFirst } from "../../../../hooks/useNameFormat";
import { FONT_SIZE, INK } from "../../../../theme/tokens";
import { formatPersonFullName } from "../../../../utils/formatters";
import type { MergeCandidate, MergeCandidatePage } from "../../../../utils/nurseMergeModels";
import { MergeRoleChip } from "./MergeRoleChip";

type Props = {
	currentNurseName: string;
	departmentId: string;
	query: string;
	onQueryChange: (next: string) => void;
	/** True while the typed query hasn't reached the search yet — the list is about to change. */
	isSettling: boolean;
	page: MergeCandidatePage | undefined;
	isLoading: boolean;
	isError: boolean;
	onRetry: () => void;
	onSelect: (candidate: MergeCandidate) => void;
};

/** Step 1: find the other record. Picking a row IS the answer — it goes straight to the comparison. */
export const MergeFindStep: FC<Props> = ({ currentNurseName, departmentId, query, onQueryChange, isSettling, page, isLoading, isError, onRetry, onSelect }) => {
	const { t } = useTranslation();
	const lastNameFirst = useLastNameFirst();
	const hasQuery = query.trim().length > 0;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} data-testid="merge-employees-find-step">
			<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, color: INK.SECONDARY, lineHeight: 1.5 }}>
				{t("nurse.merge.find.intro", { name: currentNurseName })}
			</Typography>
			<SheetSearchField
				value={query}
				onChange={onQueryChange}
				placeholder={t("nurse.merge.find.placeholder")}
				name="merge-employees-search"
				dataTestId="merge-employees-search-input"
			/>
			{!hasQuery ? (
				<EmptyState
					icon={<PersonSearchOutlined />}
					title={t("nurse.merge.find.idle.title")}
					hint={t("nurse.merge.find.idle.hint")}
					dataTestId="merge-employees-find-idle"
				/>
			) : isError ? (
				<EmptyState
					icon={<ErrorOutlineRounded />}
					iconColor="error.main"
					title={t("nurse.merge.find.error.title")}
					hint={t("nurse.merge.find.error.hint")}
					action={
						<SystemButton buttonVariant="secondary" size="small" onClick={onRetry} data-testid="merge-employees-search-retry">
							{t("nurse.merge.retry")}
						</SystemButton>
					}
					dataTestId="merge-employees-find-error"
				/>
			) : isLoading || isSettling || !page ? (
				<Box
					sx={{ display: "flex", justifyContent: "center", py: 4 }}
					data-testid="merge-employees-find-loading"
					role="status"
					aria-label={t("nurse.merge.find.loading")}
				>
					<JumpingDots size={6} />
				</Box>
			) : page.items.length === 0 ? (
				<EmptyState
					icon={<SearchOffRounded />}
					title={t("nurse.merge.find.empty.title")}
					hint={t("nurse.merge.find.empty.hint")}
					dataTestId="merge-employees-find-empty"
				/>
			) : (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
					<SystemChoiceList
						ariaLabel={t("nurse.merge.find.results")}
						dataTestId="merge-employees-candidates"
						onSelect={(value) => {
							const chosen = page.items.find((candidate) => String(candidate.nurseId) === value);
							if (chosen) onSelect(chosen);
						}}
						items={page.items.map((candidate) => ({
							value: String(candidate.nurseId),
							label: t("nurse.merge.find.candidate.aria", { name: formatPersonFullName(candidate, lastNameFirst), id: candidate.nurseId }),
							dataTestId: `merge-employees-candidate-${candidate.nurseId}`,
							content: <CandidateRowContent candidate={candidate} departmentId={departmentId} />,
						}))}
					/>
					{page.hasMore && (
						<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }} data-testid="merge-employees-find-more">
							{t("nurse.merge.find.more", { count: page.items.length })}
						</Typography>
					)}
				</Box>
			)}
		</Box>
	);
};

/** One candidate: who they are, where they stand here, and how to reach them. Phrasing content only — it sits in a `<button>`. */
const CandidateRowContent: FC<{ candidate: MergeCandidate; departmentId: string }> = ({ candidate, departmentId }) => {
	const { t } = useTranslation();
	const lastNameFirst = useLastNameFirst();
	const isMemberHere = candidate.departmentIds.includes(departmentId);
	const isActiveHere = candidate.activeDepartmentIds.includes(departmentId);
	const contact = [candidate.email, candidate.phoneNumber].filter((value): value is string => !!value).join(" · ");

	return (
		<Box component="span" sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
			<Box component="span" sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1, rowGap: 0.5 }}>
				<Box component="span" sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700, color: INK.PRIMARY }}>
					{formatPersonFullName(candidate, lastNameFirst)}
				</Box>
				<Box component="span" sx={{ fontSize: FONT_SIZE.CAPTION, fontWeight: 500, color: INK.MUTED, fontVariantNumeric: "tabular-nums" }}>
					#{candidate.nurseId}
				</Box>
				{candidate.role && <MergeRoleChip role={candidate.role} />}
				{isMemberHere ? (
					<MetaChip label={t(isActiveHere ? "nurse.merge.find.active.here" : "nurse.merge.find.inactive.here")} tone={isActiveHere ? "success" : "neutral"} />
				) : (
					<MetaChip
						label={
							candidate.activeDepartmentIds.length > 0
								? t("nurse.merge.find.other.active", { count: candidate.activeDepartmentIds.length })
								: t("nurse.merge.find.other.inactive")
						}
						tone="info"
					/>
				)}
				<MetaChip label={t(candidate.hasLogin ? "nurse.merge.login.has" : "nurse.merge.login.none")} tone={candidate.hasLogin ? "primary" : "neutral"} />
			</Box>
			<Box component="span" sx={{ fontSize: FONT_SIZE.CAPTION, fontWeight: 400, color: INK.MUTED, overflowWrap: "anywhere" }}>
				{/* DEFAULT: a record with neither phone nor email says so, rather than leaving a blank line. */}
				{contact || t("nurse.merge.find.no.contact")}
			</Box>
		</Box>
	);
};
